import React from 'react';
import { MerchantInvoice, MerchantBillingSettings } from '../../../types/gst';
import { QrCode } from 'lucide-react';

interface Props {
  invoice: MerchantInvoice;
  settings?: MerchantBillingSettings;
}

export const StylishModernTemplate: React.FC<Props> = ({ invoice, settings }) => {
  const sellerName = invoice.sellerTradeName || invoice.sellerLegalName || settings?.companyName || 'Business Enterprise';
  const sellerGstin = invoice.sellerGstin || settings?.gstin || '';
  const sellerAddress = invoice.sellerAddress || settings?.address || '';
  const sellerPhone = invoice.sellerPhone || settings?.phone || '';
  const upiId = settings?.upiId || 'merchant@upi';

  return (
    <div className="bg-white text-slate-800 p-8 font-sans max-w-4xl mx-auto border border-teal-200 shadow-md rounded-2xl text-xs print:p-0 print:border-none print:shadow-none">
      {/* Top Stylish Banner */}
      <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-cyan-600 -m-8 mb-6 p-6 rounded-t-2xl text-white flex justify-between items-center print:m-0 print:rounded-none">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-teal-100">Original Tax Invoice</div>
          <h1 className="text-2xl font-black tracking-tight">{sellerName}</h1>
          <p className="text-teal-100 text-[11px] mt-0.5">{sellerAddress}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black font-mono">#{invoice.invoiceNumber}</div>
          <div className="text-teal-100 text-xs">Date: {invoice.invoiceDate}</div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-3 bg-teal-50/60 rounded-xl border border-teal-100">
          <div className="text-[10px] font-bold uppercase text-teal-800 tracking-wider">Merchant Info</div>
          {sellerGstin && <div className="mt-1 font-mono font-semibold text-slate-800 text-[11px]">GSTIN: {sellerGstin}</div>}
          {sellerPhone && <div className="text-slate-600">Phone: {sellerPhone}</div>}
          <div className="text-slate-600">State: {invoice.sellerStateCode}</div>
        </div>

        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-[10px] font-bold uppercase text-slate-600 tracking-wider">Billed To</div>
          <div className="font-bold text-slate-900 text-sm mt-0.5">{invoice.customerName}</div>
          {invoice.customerGstin && <div className="font-mono text-teal-800 font-semibold text-[11px]">GSTIN: {invoice.customerGstin}</div>}
          <div className="text-slate-600 truncate">{invoice.customerBillingAddress || 'Address on file'}</div>
        </div>

        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center">
          <div>
            <div className="text-[10px] font-bold uppercase text-slate-600 tracking-wider">Amount Due</div>
            <div className="text-lg font-black text-teal-900 font-mono mt-0.5">
              ₹{invoice.balanceDue > 0 ? invoice.balanceDue.toLocaleString('en-IN') : '0.00'}
            </div>
            <div className="text-[10px] text-slate-500">
              {invoice.balanceDue > 0 ? `Due by ${invoice.dueDate || 'Immediate'}` : 'Paid in Full'}
            </div>
          </div>
          {upiId && (
            <div className="p-1.5 bg-white rounded-lg border border-teal-200 shadow-sm text-center">
              <QrCode className="h-8 w-8 text-teal-700 mx-auto" />
              <div className="text-[8px] font-mono text-slate-600 mt-0.5">Scan UPI</div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <table className="w-full text-left mb-6">
        <thead>
          <tr className="border-b-2 border-teal-600 text-teal-900 text-[10px] font-bold uppercase">
            <th className="py-2.5 px-3">Item & Description</th>
            <th className="py-2.5 px-2 text-center">HSN</th>
            <th className="py-2.5 px-2 text-right">Qty</th>
            <th className="py-2.5 px-2 text-right">Rate</th>
            <th className="py-2.5 px-2 text-right">Taxable</th>
            <th className="py-2.5 px-2 text-right">GST</th>
            <th className="py-2.5 px-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {invoice.lineItems.map((item, idx) => (
            <tr key={idx} className="hover:bg-teal-50/30">
              <td className="py-2.5 px-3">
                <div className="font-semibold text-slate-900">{item.description}</div>
                {item.discountAmount > 0 && (
                  <div className="text-[10px] text-emerald-600">Discount: -₹{item.discountAmount.toFixed(2)}</div>
                )}
              </td>
              <td className="py-2.5 px-2 text-center font-mono text-slate-500">{item.hsnSac || '—'}</td>
              <td className="py-2.5 px-2 text-right font-mono">{item.quantity} {item.unit}</td>
              <td className="py-2.5 px-2 text-right font-mono">₹{item.unitPrice.toLocaleString('en-IN')}</td>
              <td className="py-2.5 px-2 text-right font-mono">₹{item.taxableAmount.toLocaleString('en-IN')}</td>
              <td className="py-2.5 px-2 text-right font-mono text-teal-700 font-semibold">{item.gstRate}%</td>
              <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">₹{item.totalAmount.toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals & Sign */}
      <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-200">
        <div className="space-y-3">
          <div className="p-3 bg-slate-50 rounded-xl text-[11px] space-y-1">
            <div className="font-bold text-slate-700">Payment Modes & Bank:</div>
            <div>Bank: {settings?.bankName || 'HDFC Bank'} &bull; A/C: {settings?.accountNumber || '50200088991122'}</div>
            <div>IFSC: {settings?.ifscCode || 'HDFC0000240'} &bull; UPI: {upiId}</div>
          </div>
          <div className="text-[10px] text-slate-500 whitespace-pre-line">
            {invoice.termsAndConditions || settings?.defaultTerms}
          </div>
        </div>

        <div className="space-y-1 text-slate-700">
          <div className="flex justify-between py-1">
            <span>Taxable Total:</span>
            <span className="font-mono">₹{invoice.subtotalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between py-1 text-teal-800">
            <span>Total GST Tax:</span>
            <span className="font-mono font-medium">₹{invoice.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="border-t-2 border-teal-600 pt-2 flex justify-between font-black text-base text-teal-950">
            <span>Grand Total:</span>
            <span className="font-mono">₹{invoice.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="pt-8 text-right">
            <div className="font-bold text-slate-800">{sellerName}</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Authorized Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
};
